// Groups: a tile holding other tiles (links, webtop links and other groups, so groups nest like folders), drawn as a
// mini grid of their icons. Grouping and ungrouping, joining by drop, and the open group's panel, where members
// open, reorder, move into a subgroup or up the folder path, or are dragged out.
import { EDIT, lineIcon, saveData } from '../widgets/kit.js';
import { cellsOf, GRID, moveTo, pageX, pageY, pinDrawn, place, reading, settle, sizeKey, sizeOf, snap, spotFields, spotOf, tilePos, viewportOn } from './geometry.js';
import { DRAG_THRESHOLD, longPressed } from './input.js';
import { showMenu } from './menu.js';
import { overTrash, showTrash } from './notch.js';
import { closeOverlay, openOverlay, overlay } from './overlay.js';
import { render, setSelection, tileEl } from './render.js';
import { duplicateTiles, openDialog, removeShortcuts } from './shortcuts.js';
import { API, byId, currentRole, $webtop, webtop } from './state.js';
import {
  canJoin, groupItems, groupPath, iconNode, isGroup, onWebtop, openShortcut, parentGroup, tileIcon,
} from './tiles.js';
import { api, modal, toast } from './ui.js';

// A group is a tile that holds other tiles (see GROUPABLE): a folder-like tile (1x1, 2x1 or 3x1) showing a mini grid
// of its first members' icons (two rows, two columns per cell of width; a subgroup shows as a small folder) and its
// title (or none). Opening it shows its members in a panel, where they open, have their own menu, and can be dragged
// out onto the webtop (they leave the group), onto a subgroup (they go into it) or onto a group in the folder path
// at the top (they move up to it); opening a subgroup there shows its members in the same panel. Selecting tiles and
// choosing Group makes one (one selected group takes the other tiles in; several selected groups go into a new one);
// dropping a tile onto a group adds it. A group whose last member leaves is deleted. Membership lives in the
// group's data.items.
export function groupTile(s, el) {
  const items = groupItems(s);
  el.classList.add('group');
  el.classList.toggle('untitled', !s.title); // no name: the icons take the title's room too
  el.setAttribute('role', 'button');
  el.title = `${s.title || 'Group'}: ${items.length}`;
  // Room for two rows of previews, two columns per cell of width: 4, 8 or 12 members show. They sit in one row when
  // they fit in one, else in two even rows (5 as 3 + 2), centred.
  const cols = 2 * cellsOf(sizeKey(s))[0];
  const shown = items.slice(0, cols * 2);
  const perRow = Math.max(1, Math.ceil(shown.length / (shown.length <= cols ? 1 : 2)));
  const icons = document.createElement('div');
  icons.className = 'icon group-icons';
  icons.style.setProperty('--cols', cols);
  icons.style.setProperty('--per-row', perRow);
  const flow = document.createElement('div');
  flow.className = 'group-flow';
  for (const m of shown) flow.append(isGroup(m) ? folderIcon() : iconNode(tileIcon(m), 'group-icon'));
  icons.append(flow);
  const title = document.createElement('div');
  title.className = 'title';
  title.textContent = s.title || '';
  el.append(icons, title);
  return el;
}
// A subgroup's preview inside a group: a folder outline, drawn in the text colour.
function folderIcon() {
  const el = document.createElement('div');
  el.className = 'group-icon folder';
  el.innerHTML = lineIcon('folder');
  return el;
}
export function groupUnder(x, y, except) {
  const el = document.elementsFromPoint(x, y).map((e) => e.closest?.('#webtop .tile.group'))
    .find((t) => t && !except.has(t.dataset.id));
  return el ? byId(el.dataset.id) : null;
}
// Saves a group's member list; a group left empty is deleted (and leaves the group it was in).
export async function setGroupItems(g, items) {
  if (!items.length) {
    const parent = parentGroup(g);
    await api('DELETE', `${API}/shortcuts/${g.id}`).catch(toast);
    webtop.shortcuts = webtop.shortcuts.filter((x) => x.id !== g.id);
    if (parent) await setGroupItems(parent, parent.data.items.filter((id) => id !== g.id));
    if (openGroupId === g.id) { if (parent && byId(parent.id)) openGroupId = parent.id; else closeGroup(); }
    return;
  }
  await saveData(g, { items });
}
// Moves tiles into g (out of whatever group they were in). Tiles that cannot go in (g itself, or a group g is
// inside) stay where they are.
export async function joinGroup(g, list) {
  const fits = list.filter((s) => canJoin(g, s));
  if (fits.length < list.length) {
    toast(fits.length ? 'Some tiles could not go into that group' : 'That cannot go into that group', { kind: 'info' });
  }
  if (!fits.length) return render();
  const ids = fits.map((s) => s.id);
  for (const other of webtop.shortcuts.filter((x) => isGroup(x) && x !== g)) { // a tile is in one group at most
    const left = other.data?.items?.filter((id) => !ids.includes(id));
    if (left && left.length !== other.data.items.length) await setGroupItems(other, left);
  }
  await setGroupItems(g, [...new Set([...(g.data?.items ?? []), ...ids])]);
  const top = onWebtop().includes(g);
  if (top) setSelection([g.id]);
  render(top ? [g] : []);
}
export async function groupTiles(list) {
  const groups = list.filter(isGroup);
  const loose = list.filter((s) => !isGroup(s)).sort(reading);
  try {
    if (groups.length === 1 && loose.length) return await joinGroup(groups[0], loose);
    // A new group, where the first of the tiles (in reading order) was, holding them all (groups as subgroups).
    const all = [...list].sort((a, b) => reading(spotOf(a), spotOf(b)));
    const fields = { kind: 'group', title: null, ...spotFields(spotOf(all[0])), data: { items: [] } };
    const target = await api('POST', `${API}/shortcuts`, fields);
    webtop.shortcuts.push(target);
    await joinGroup(target, all);
  } catch (e) { toast(e); }
}
// Free spots for tiles leaving a group: around `near` (the group, or where one was dropped), clear of the tiles
// drawn on the webtop and of each other. The spots are saved, since the user moved the tiles out.
async function placeOnWebtop(list, near) {
  const taken = [...$webtop.querySelectorAll('.tile')].map((t) => ({ ...tilePos(t), ...sizeOf(byId(t.dataset.id)) }));
  const updates = [];
  for (const s of list) {
    const size = sizeOf(s);
    const p = viewportOn() ? settle(snap(near.x, near.y, size, false, true), taken, size) : place(snap(near.x, near.y, size), taken, size);
    taken.push({ ...p, ...size });
    updates.push(moveTo(s, p));
  }
  updates.push(...pinDrawn(list));
  if (updates.length) await api('PATCH', `${API}/shortcuts`, updates).catch(toast);
}
// Remove from Group moves a tile up one level: into the group around its group, right after it, or onto the
// webtop. `near` (a drop point) sends it straight to the webtop.
async function removeFromGroup(m, near) {
  const g = parentGroup(m);
  if (!g) return;
  const up = near ? null : parentGroup(g);
  // Out of the old group before into the new one: the server keeps a tile listed by two groups in the first of
  // them (store.tidyGroups), which may be the one it is leaving.
  await setGroupItems(g, g.data.items.filter((id) => id !== m.id));
  if (up && byId(up.id)) { // the group around it may have gone too, when g was all it held
    const items = [...up.data.items];
    const at = items.indexOf(g.id); // gone when g was emptied
    items.splice(at < 0 ? items.length : at + 1, 0, m.id);
    await setGroupItems(up, items);
    return render();
  }
  const at = spotOf(g);
  await placeOnWebtop([m], near ?? { x: at.x + GRID.w, y: at.y });
  render([m]);
}
// Ungroup puts a group's members where the group was: in its place in the group around it, or on the webtop.
export async function ungroup(g) {
  const items = groupItems(g);
  const up = parentGroup(g);
  if (up) {
    const list = [...up.data.items];
    list.splice(list.indexOf(g.id), 1, ...items.map((s) => s.id));
    await setGroupItems(g, []); // deleted first, so its members are listed by one group only (see removeFromGroup)
    await setGroupItems(up, list);
    return render();
  }
  await setGroupItems(g, []);
  await placeOnWebtop(items, g);
  setSelection(items.map((s) => s.id));
  render(items);
}
const $groupDlg = modal(document.getElementById('group-dlg'));
const $groupForm = document.getElementById('group-form');
let renaming = null;
export function renameGroup(g) {
  renaming = g;
  $groupForm.reset();
  $groupForm.elements.title.value = g.title || '';
  $groupDlg.showModal();
  $groupForm.elements.title.focus();
}

// The open group's panel: a full-screen view of its members, an overlay like the launcher (client/lib/overlay.js). A click
// anywhere but its title or a member closes it; Escape goes up a level. render() redraws it while it is open, and
// opening another group while it is open (a subgroup, a crumb, a level up) redraws it from the top.
const $groupPanel = document.getElementById('group-panel');
let panel = null; // the overlay, made in init()
export let openGroupId = null;
export function openGroup(g) {
  openGroupId = g.id;
  openOverlay(panel);
}
export function closeGroup() {
  closeOverlay(panel);
  openGroupId = null;
}
// One level up from the open group: the group around it, or closed when it is on the webtop.
function groupUp() {
  const up = parentGroup(byId(openGroupId) ?? {});
  if (up) openGroup(up); else closeGroup();
}
export function drawGroupPanel() {
  const g = byId(openGroupId);
  if (!g) return closeGroup();
  // The folder path: the groups above this one are buttons that go up to them (and take tiles dropped on them).
  const head = document.createElement('div');
  head.className = 'group-panel-title';
  for (const p of groupPath(g).slice(0, -1)) {
    const crumb = document.createElement('button');
    crumb.type = 'button';
    crumb.className = 'crumb';
    crumb.dataset.id = p.id;
    crumb.textContent = p.title || 'Group';
    crumb.addEventListener('click', () => openGroup(p));
    const sep = document.createElement('span');
    sep.className = 'crumb-sep';
    sep.textContent = '›';
    head.append(crumb, sep);
  }
  const name = document.createElement('span');
  name.textContent = g.title || 'Group';
  head.append(name);
  const grid = document.createElement('div');
  grid.className = 'group-panel-grid';
  for (const m of groupItems(g)) grid.append(memberTile(m));
  $groupPanel.replaceChildren(head, grid);
}
function memberTile(m) {
  const el = tileEl(m, { x: 0, y: 0 });
  el.style.left = el.style.top = ''; // laid out by the panel's grid
  el.pos = null;
  el.classList.remove('selected');
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') openShortcut(m); });
  el.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGroup(m)) {
      return showMenu(e.clientX, e.clientY, [
        { label: m.title || 'Group', cls: 'head' },
        { label: 'Open', action: () => openGroup(m) },
        { label: 'Edit', quick: 'edit', roles: EDIT, action: () => renameGroup(m) },
        { label: 'Ungroup', roles: EDIT, action: () => ungroup(m) },
        { label: 'Remove from Group', roles: EDIT, action: () => removeFromGroup(m) },
        { label: 'Duplicate', quick: 'copy', roles: EDIT, action: () => duplicateTiles([m]) },
        { label: 'Delete', quick: 'trash', cls: 'danger', roles: EDIT, action: () => removeShortcuts([m]) },
      ]);
    }
    showMenu(e.clientX, e.clientY, [
      { label: m.title, cls: 'head' },
      { label: 'Open', action: () => openShortcut(m) },
      { label: 'Edit', quick: 'edit', roles: EDIT, action: () => openDialog(m) },
      { label: 'Remove from Group', roles: EDIT, action: () => removeFromGroup(m) },
      { label: 'Duplicate', quick: 'copy', roles: EDIT, action: () => duplicateTiles([m]) },
      { label: 'Delete', quick: 'trash', cls: 'danger', roles: EDIT, action: () => removeShortcuts([m]) },
    ]);
  });
  el.addEventListener('pointerdown', (e) => dragMember(e, m, el));
  return el;
}
// A member is opened by a tap, or (editors) dragged: dropped on a subgroup it goes into it, on a group in the folder
// path it moves up to that group, on another member it takes that member's place; dropped outside the members' grid
// it leaves the group, the panel closes and the tile stays where it was dropped.
function dragMember(e, m, el) {
  if (e.button !== 0) return;
  e.preventDefault();
  const start = { x: e.clientX, y: e.clientY };
  let ghost = null;
  el.setPointerCapture(e.pointerId);
  const move = (ev) => {
    if (longPressed || currentRole() !== 'editor') return;
    if (!ghost && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_THRESHOLD) return;
    if (!ghost) {
      const r = el.getBoundingClientRect();
      ghost = el.cloneNode(true);
      ghost.classList.add('group-ghost', 'dragging');
      Object.assign(ghost.style, { width: `${r.width}px`, height: `${r.height}px` });
      document.body.append(ghost);
      el.classList.add('leaving');
      showTrash(true);
    }
    overTrash(ev.clientX, ev.clientY);
    const into = dropInto(ev, m, el);
    for (const t of $groupPanel.querySelectorAll('.drop-into')) if (t !== into) t.classList.remove('drop-into');
    into?.classList.add('drop-into');
    ghost.style.left = `${ev.clientX - ghost.offsetWidth / 2}px`;
    ghost.style.top = `${ev.clientY - ghost.offsetHeight / 2}px`;
  };
  const end = async (ev) => {
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', end);
    el.removeEventListener('pointercancel', end);
    if (!ghost) {
      if (ev.type === 'pointerup' && !longPressed) openShortcut(m);
      return;
    }
    ghost.remove();
    el.classList.remove('leaving');
    for (const t of $groupPanel.querySelectorAll('.drop-into')) t.classList.remove('drop-into');
    const binned = ev.type === 'pointerup' && overTrash(ev.clientX, ev.clientY);
    showTrash(false);
    if (binned) return removeShortcuts([m], { ask: false }); // dropped on the notch's trash
    const into = dropInto(ev, m, el);
    if (into) return joinGroup(byId(into.dataset.id), [m]);
    const g = byId(openGroupId);
    const r = el.parentElement.getBoundingClientRect(); // the members' grid
    if (ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom) {
      const over = document.elementsFromPoint(ev.clientX, ev.clientY).map((t) => t.closest?.('.group-panel .tile'))
        .find((t) => t && t !== el);
      if (!over || !g) return;
      const items = g.data.items.filter((id) => id !== m.id);
      items.splice(items.indexOf(over.dataset.id), 0, m.id);
      await setGroupItems(g, items);
      return render();
    }
    closeGroup();
    const size = sizeOf(m);
    await removeFromGroup(m, { x: pageX(ev.clientX) - size.w / 2, y: pageY(ev.clientY) - size.h / 2 });
  };
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);
}
// The subgroup tile or folder-path group under the pointer that the dragged member m may go into, if any.
function dropInto(ev, m, el) {
  const t = document.elementsFromPoint(ev.clientX, ev.clientY)
    .map((n) => n.closest?.('.group-panel .crumb, .group-panel .tile.group')).find((n) => n && n !== el);
  const g = t && byId(t.dataset.id);
  return g && canJoin(g, m) ? t : null;
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  $groupForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const g = renaming;
    $groupDlg.close();
    try {
      const title = $groupForm.elements.title.value.trim() || null;
      Object.assign(g, await api('PATCH', `${API}/shortcuts/${g.id}`, { title }));
      render();
    } catch (err) { toast(err); }
  });
  panel = overlay({
    el: $groupPanel,
    keep: '.tile, .group-panel-title',
    onOpen: drawGroupPanel,
    onClose: () => { openGroupId = null; },
    onEscape: groupUp, // a level up, closing the panel from a group on the webtop
  });
}
